<template>
  <div>
    <filter-manager
      ref="filter"
      v-model="filter.model"
      :filters="filtersHeader"
      class="filter-manager_extended"
      search-container-class="search-btn"
      :display-advanced="false"
      @search="onSearchHandler()"
    />

    <custom-separator
      class="my-3 custom-separator"
      text="Resultados de Equipos"
    />
    <div v-if="hasResults">
      <result-row
        v-for="(item, index) in results"
        :key="index"
        :referral="referral"
        :item="item"
        :recommendation="recommendation"
        :inventory-flow="true"
        :out-of-my-inventory="outOfMyInventory"
        :editable="inventoryEditable"
        @open="handlerOpen"
        @close="handlerClose"
        @refresh="refreshPage"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="onPageChange"
      />
    </div>
    <custom-result-not-found v-else text="No hay Información Disponible." />
  </div>
</template>
<script>
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import inventoryCatalogService from '@/services/api/inventorycatalog.api';
import locationService from '@/services/api/location.api';
import purchaseMethodInventoryTypes from '@/utils/constants/purchaseMethodInventoryTypes';
import ResultRow from '@/views/inventory/equipmentMaintenance/inventoryManagement/components/ResultRow.vue';
import peiATServiceStatus from '@/utils/constants/peiATServiceStatus';

const DEFAULT_PAGE_SIZE = 5;

export default {
  name: 'MyInventory',
  components: {
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    Pagination,
  },
  props: {
    catalogId: {
      type: Number,
      default: null,
    },
    referral: {
      type: Object,
      required: false,
      default: null,
    },
    recommendation: {
      type: Object,
      default: () => {},
      required: false,
    },
    outOfMyInventory: {
      type: Boolean,
      default: false,
    },
    caatId: {
      type: Number,
      default: null,
    },
  },
  data: () => ({
    filter: {
      model: {
        type: null,
        caat: null,
        status: null,
        keyword: null,
      },
      options: {
        types: [],
        caats: [],
        statuses: [],
      },
    },
    pagination: {
      count: 0,
      currentPage: 1,
      pageCount: 1,
      totalItemCount: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
  }),
  computed: {
    filtersHeader() {
      return [
        {
          name: 'type',
          component: 'CustomDropDown',
          options: this.filter.options.types,
          value: this.filter.model.type,
          placeholder: 'Tipo de Inventario',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-12 col-md-3 mb-md-0 mb-2',
        },
        {
          name: 'caat',
          component: 'CustomDropDown',
          options: this.filter.options.caats,
          value: this.filter.model.caat,
          placeholder: 'CAAT',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-12 col-md-3 mb-md-0 mb-2',
          visible: this.outOfMyInventory,
        },
        {
          name: 'keyword',
          component: 'CustomInput',
          value: this.filter.model.keyword,
          placeholder: 'Escriba Nombre de Equipo o Código de Equipo',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-12 col-sm-12 col-md',
          type: 'text',
        },
      ];
    },
    inventoryTypes() {
      return [
        {
          id: purchaseMethodInventoryTypes.New,
          name: 'Nuevo',
        },
        {
          id: purchaseMethodInventoryTypes.Used,
          name: 'Usado',
        },
        {
          id: purchaseMethodInventoryTypes.Auction,
          name: 'Subasta',
        },
      ];
    },
    hasResults() {
      return Boolean(this.results?.length);
    },
    inventoryEditable() {
      return [
        peiATServiceStatus.PendienteAnalisisRequisicion,
        peiATServiceStatus.PendienteDeClasificacion,
      ].includes(this.referral?.serviceStatusId);
    },
  },
  watch: {
    catalogId(newValue) {
      if (newValue && this.recommendation.id) {
        this.onSearchHandler();
      }
    },
  },
  async mounted() {
    this.getInventoryManagementFilters();
    await this.onSearchHandler();
  },
  methods: {
    async onSearchHandler(page = 1) {
      if (!this.catalogId || !this.recommendation.id) return;

      const { caat, type, keyword } = this.filter.model;

      let caatId = this.caatId;
      let excludeCaatId = null;

      if (this.outOfMyInventory) {
        caatId = caat?.id;
        excludeCaatId = this.caatId;
      } else {
        caatId = this.caatId ? this.caatId : 0;
      }

      const { data } = await inventoryCatalogService.getSearchEquipments(
        this.catalogId,
        this.recommendation.id,
        {
          excludeCaatId: excludeCaatId,
          caatId: caatId,
          inventoryTypeId: type?.id,
          keyword,
          currentPage: page,
          pageSize: this.pagination.pageSize,
        }
      );

      this.results = data.data.map((x) => ({ ...x, isOpen: false }));

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async getInventoryManagementFilters() {
      const [statuses, caats] = await Promise.all([
        inventoryCatalogService.getEquipmentStatus(),
        locationService.getCaats(),
      ]);
      this.filter.options.statuses = statuses.data;
      this.filter.options.caats = caats.data.filter(
        (x) => x.id !== this.caatId
      );
      this.filter.options.types = this.inventoryTypes;
    },
    handlerOpen(item) {
      this.results
        .filter((x) => x.id !== item.id && x.isOpen)
        .forEach((x) => (x.isOpen = false));
      item.isOpen = true;
    },
    handlerClose(item) {
      item.isOpen = false;
      this.onPageChange(this.currentPage);
    },
    async onPageChange(page) {
      this.onSearchHandler(page);
    },
    refreshPage(item) {
      this.handlerClose(item);
      this.$emit('refresh');
    },
  },
};
</script>
<style lang="scss"></style>
